import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Unterschriftsfeld für Finger, Stift und Maus. Liefert ein PNG (transparenter Hintergrund) als Data-URL,
 * sobald genug gezeichnet wurde – sonst null. Die Fläche skaliert mit devicePixelRatio (scharf auf Handys).
 */
export function Unterschrift({ beiAenderung }: { beiAenderung: (png: string | null) => void }) {
  const flaeche = useRef<HTMLCanvasElement>(null);
  const zeichnet = useRef(false);
  const letzter = useRef<{ x: number; y: number } | null>(null);
  const strecke = useRef(0);
  const [leer, setLeer] = useState(true);
  // Als Ref, damit ein neuer Callback beim Neu-Rendern die Unterschrift nicht löscht.
  const melden = useRef(beiAenderung);
  melden.current = beiAenderung;

  const vorbereiten = useCallback(() => {
    const canvas = flaeche.current;
    if (!canvas) return;
    const verhaeltnis = window.devicePixelRatio || 1;
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = Math.round(width * verhaeltnis);
    canvas.height = Math.round(height * verhaeltnis);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(verhaeltnis, verhaeltnis);
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0B0B14";
    strecke.current = 0;
    setLeer(true);
    melden.current(null);
  }, []);

  useEffect(() => {
    vorbereiten();
    // Drehen des Handys ändert die Größe – dann neu beginnen statt verzerrt weiterzuzeichnen.
    window.addEventListener("orientationchange", vorbereiten);
    return () => window.removeEventListener("orientationchange", vorbereiten);
  }, [vorbereiten]);

  function punkt(e: React.PointerEvent<HTMLCanvasElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    zeichnet.current = true;
    letzter.current = punkt(e);
  }

  function bewegen(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!zeichnet.current || !letzter.current) return;
    const ctx = e.currentTarget.getContext("2d");
    if (!ctx) return;
    const p = punkt(e);
    ctx.beginPath();
    ctx.moveTo(letzter.current.x, letzter.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    strecke.current += Math.hypot(p.x - letzter.current.x, p.y - letzter.current.y);
    letzter.current = p;
  }

  function ende() {
    if (!zeichnet.current) return;
    zeichnet.current = false;
    letzter.current = null;
    // Ein Punkt oder Strich reicht nicht als Unterschrift.
    const genug = strecke.current > 120;
    setLeer(!genug);
    melden.current(genug && flaeche.current ? flaeche.current.toDataURL("image/png") : null);
  }

  return (
    <div>
      <div className="relative">
        <canvas
          ref={flaeche}
          aria-label="Unterschriftsfeld"
          className="h-44 w-full touch-none rounded-xl border-2 border-dashed border-neutral-300 bg-white"
          onPointerDown={start}
          onPointerMove={bewegen}
          onPointerUp={ende}
          onPointerCancel={ende}
          onPointerLeave={ende}
        />
        {leer && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-neutral-400">
            Hier mit dem Finger unterschreiben
          </span>
        )}
        <span className="pointer-events-none absolute bottom-8 left-6 right-6 border-b border-neutral-300" aria-hidden />
      </div>
      <button type="button" onClick={vorbereiten} className="mt-2 text-sm font-semibold text-neutral-600 underline underline-offset-2">
        Unterschrift löschen
      </button>
    </div>
  );
}
